import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  checkConnectivityNow,
  getConnectivitySnapshot,
  getServerConnectivitySnapshot,
  startConnectivityMonitor,
  subscribeConnectivity,
} from '../lib/connectivity'

export interface OnlineStatus {
  /** The network answered the last time we asked. */
  online: boolean
  /** Convenience inverse — reads better at call sites that gate on being down. */
  offline: boolean
  /** A connectivity probe is in flight (i.e. "Reconnecting…"). */
  checking: boolean
  /** Epoch ms of the last online/offline transition; 0 before the first one. */
  since: number
  /** Force a probe now — wired to "Try again" buttons. */
  recheck: () => Promise<boolean>
}

/**
 * Subscribe to real connectivity (see `lib/connectivity.ts` for why
 * `navigator.onLine` is not enough on its own).
 */
export function useOnlineStatus(): OnlineStatus {
  const snapshot = useSyncExternalStore(
    subscribeConnectivity,
    getConnectivitySnapshot,
    getServerConnectivitySnapshot,
  )

  useEffect(() => startConnectivityMonitor(), [])

  const recheck = useCallback(() => checkConnectivityNow(), [])

  return {
    online: snapshot.status === 'online',
    offline: snapshot.status === 'offline',
    checking: snapshot.checking,
    since: snapshot.since,
    recheck,
  }
}

/**
 * Run `callback` when the connection comes back after having been lost.
 *
 * Pages use this to refetch whatever failed while the user was in a tunnel, so
 * coming back up heals the screen on its own rather than leaving a stale error
 * the user has to dismiss. Deliberately does NOT fire on mount — only on a
 * genuine offline → online transition.
 */
export function useRetryOnReconnect(callback: () => void, enabled = true): void {
  const { online } = useOnlineStatus()
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  // Seeded from the current status so a component mounted while already
  // offline still fires once the connection returns.
  const wasOfflineRef = useRef(!online)

  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true
      return
    }
    if (wasOfflineRef.current) {
      wasOfflineRef.current = false
      if (enabled) callbackRef.current()
    }
  }, [online, enabled])
}

/**
 * True for a few seconds after the connection is restored, so the UI can
 * confirm recovery and then get out of the way.
 */
export function useJustReconnected(durationMs = 4000): boolean {
  const { online } = useOnlineStatus()
  const [visible, setVisible] = useState(false)
  const wasOfflineRef = useRef(!online)

  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true
      setVisible(false)
      return
    }
    if (!wasOfflineRef.current) return

    wasOfflineRef.current = false
    setVisible(true)
    const timer = setTimeout(() => setVisible(false), durationMs)
    return () => clearTimeout(timer)
  }, [online, durationMs])

  return visible
}
